(() => {
  if (customElements.get('reefline-predictive-search')) return;

  class ReeflinePredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results) return;
      this.resultsId = this.results.id;
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.controller = null;
      this.timer = null;
      this.activeIndex = -1;
      this.onInput = this.onInput.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('focusout', this.onFocusOut);
    }

    disconnectedCallback() {
      this.controller?.abort();
      window.clearTimeout(this.timer);
      this.input?.removeEventListener('input', this.onInput);
      this.input?.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('focusout', this.onFocusOut);
      this.initialized = false;
    }

    onInput() {
      window.clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = null;
      this.close();
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.close();
        return;
      }
      this.timer = window.setTimeout(() => this.fetchResults(term), 220);
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        const wasExpanded = this.input.getAttribute('aria-expanded') === 'true';
        this.close();
        if (wasExpanded) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      const options = this.options;
      if (!options.length) return;
      if (event.key === 'Enter') {
        if (this.activeIndex < 0) return;
        event.preventDefault();
        options[this.activeIndex].querySelector('a')?.click();
        return;
      }
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + direction + options.length) % options.length;
      this.updateActiveOption(options);
    }

    onFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      window.setTimeout(() => this.close(), 100);
    }

    get options() {
      return Array.from(this.listbox?.querySelectorAll('[role="option"]') || []);
    }

    updateActiveOption(options = this.options) {
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeIndex)));
      const active = options[this.activeIndex];
      if (!active) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    async fetchResults(term) {
      this.controller?.abort();
      this.controller = new AbortController();
      const controller = this.controller;
      this.setAttribute('aria-busy', 'true');

      try {
        const endpoint = window.routes?.predictiveSearch || '/search/suggest';
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'query,product,article,page');
        url.searchParams.set('resources[limit]', '8');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('section_id', 'predictive-search');
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const html = await response.text();
        if (this.input.value.trim() !== term || this.controller !== controller) return;
        const fragment = new DOMParser().parseFromString(html, 'text/html');
        const section = fragment.getElementById('shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search section missing from response');
        this.results.innerHTML = section.innerHTML;
        this.listbox = this.results.querySelector('[data-predictive-listbox]');
        if (!this.listbox) throw new Error('Predictive search listbox missing from response');
        this.listbox.id = `${this.input.id}-listbox`;
        this.input.setAttribute('aria-controls', this.listbox.id);
        this.input.setAttribute('aria-expanded', 'true');
        this.results.hidden = false;
        this.activeIndex = -1;
        this.options.forEach((option, index) => {
          option.id = `${this.input.id}-option-${index + 1}`;
        });
        this.updateActiveOption();
        const count = this.results.querySelector('[data-predictive-count]')?.dataset.predictiveCount;
        if (count) window.Reefline?.announce?.(count);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.close();
      } finally {
        if (this.controller === controller) this.removeAttribute('aria-busy');
      }
    }

    close() {
      this.controller?.abort();
      this.activeIndex = -1;
      this.results.hidden = true;
      this.results.innerHTML = '';
      this.listbox = null;
      this.input?.setAttribute('aria-expanded', 'false');
      if (this.resultsId) this.input?.setAttribute('aria-controls', this.resultsId);
      this.input?.removeAttribute('aria-activedescendant');
      this.removeAttribute('aria-busy');
    }
  }

  customElements.define('reefline-predictive-search', ReeflinePredictiveSearch);
})();
